import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  LocalHospital as HospitalIcon,
  ReceiptLong as ClaimsIcon,
  InsertChartOutlined as AnalyticsIcon,
  CloudDone as CloudIcon,
  TrendingUp as RevenueIcon,
} from '@mui/icons-material';

import { BRAND, PRODUCT, PILLARS, OUTCOMES, NARRATION, GRADIENT_NAVY, FONT_STACK } from '../../brand';

// ============================================================================
//  BrandPanel — the navy product panel that sits beside Setup and Login.
// ============================================================================
//
//  Deliberately a single shared component: the buyer sees this surface during
//  setup and then every morning at login, so the two must not drift apart.
//  Content is the Gigatt Apex product story — name, blurb, capability strip,
//  outcome figures — sourced from brand.js rather than hard-coded here.
//
//  Props
//    facilityName  once setup is complete, the hospital's own name is shown
//                  under the product lockup (login uses this; setup does not)
//    facilityLogo  base64/URL logo, shown in place of the generic mark
//    compact       tightens vertical rhythm for the shorter login column
//    children      optional extra block dropped above the footer

const PILLAR_ICONS = [HospitalIcon, ClaimsIcon, AnalyticsIcon, CloudIcon, RevenueIcon];

// ============================================================================
//  MobileBrandBar — the brand panel's stand-in below the `lg` breakpoint
// ============================================================================
//  The full panel is a 400px column; there is no room for it on a phone or a
//  portrait tablet. Rather than drop the branding entirely — which left small
//  screens with an unlabelled form and no clue what product they were setting
//  up — this compresses the lockup into a single navy strip.
//
//  One row, fixed height, no scroll of its own.
export function MobileBrandBar() {
  return (
    <Box
      sx={{
        display: { xs: 'flex', lg: 'none' },
        alignItems: 'center', gap: 1.5, flexShrink: 0,
        px: { xs: 2, sm: 3 }, py: 1.5,
        background: GRADIENT_NAVY,
        fontFamily: FONT_STACK,
      }}
    >
      <Box
        sx={{
          width: 34, height: 34, borderRadius: '9px', flexShrink: 0,
          background: `linear-gradient(135deg, ${BRAND.greenLight} 0%, ${BRAND.green} 100%)`,
          display: 'grid', placeItems: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: 13, color: '#fff', letterSpacing: '-.4px' }}>
          GA
        </Typography>
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 900, color: '#fff', lineHeight: 1.15, letterSpacing: '-.3px' }}>
          {PRODUCT.name}
        </Typography>
        <Typography
          sx={{
            fontSize: 9.5, fontWeight: 700, color: BRAND.greenLight,
            textTransform: 'uppercase', letterSpacing: '.6px', lineHeight: 1.3,
          }}
          noWrap
        >
          {PRODUCT.vendor}
        </Typography>
      </Box>

      {/* Long-form product name only where it will not wrap to two lines. */}
      <Typography
        sx={{
          display: { xs: 'none', sm: 'block' },
          fontSize: 10.5, color: 'rgba(255,255,255,.55)', textAlign: 'right',
          lineHeight: 1.4, maxWidth: 260,
        }}
      >
        {PRODUCT.fullName}
      </Typography>
    </Box>
  );
}

function BrandPanel({ facilityName, facilityLogo, compact = false, children }) {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        overflowY: 'auto',
        background: GRADIENT_NAVY,
        color: '#fff',
        fontFamily: FONT_STACK,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* ── Ambient background: soft green glow, medical cross watermark,
             and a faint grid. All decorative, all pointer-events:none. ── */}
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <Box
          sx={{
            position: 'absolute', top: '-12%', right: '-14%', width: 540, height: 540, borderRadius: '50%',
            background: `radial-gradient(circle, ${BRAND.green}33 0%, transparent 66%)`,
          }}
        />
        <Box
          sx={{
            position: 'absolute', bottom: '-18%', left: '-16%', width: 460, height: 460, borderRadius: '50%',
            background: `radial-gradient(circle, ${BRAND.navyLight}4D 0%, transparent 68%)`,
          }}
        />
        <svg width="300" height="300" viewBox="0 0 100 100"
          style={{ position: 'absolute', right: -34, bottom: -46, opacity: 0.05 }}>
          <path fill="#fff" d="M40 0h20v40h40v20H60v40H40V60H0V40h40z" />
        </svg>
        <Box
          sx={{
            position: 'absolute', inset: 0,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),' +
              'linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'relative', zIndex: 1, width: '100%', maxWidth: 520,
          px: { xs: 4, xl: 5 }, py: compact ? 5 : 6,
        }}
      >
        {/* ── Vendor lockup: the "GA / GIGATT APEX" mark from the site ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: compact ? 4 : 5 }}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: '11px', flexShrink: 0,
              background: `linear-gradient(135deg, ${BRAND.greenLight} 0%, ${BRAND.green} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 6px 18px ${BRAND.green}45`,
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: 17, color: '#fff', letterSpacing: '-.5px' }}>
              GA
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 16, letterSpacing: '.6px', lineHeight: 1.1 }}>
              {PRODUCT.vendor}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: 'rgba(255,255,255,.6)', letterSpacing: '.7px', textTransform: 'uppercase' }}>
              {PRODUCT.vendorTagline}
            </Typography>
          </Box>
        </Box>

        {/* ── Product headline: MEFIMS, exactly as the site sets it ── */}
        <Typography
          component="h1"
          sx={{
            fontWeight: 900, letterSpacing: '-1.5px', lineHeight: .95,
            fontSize: compact ? 'clamp(2.6rem,5vw,3.4rem)' : 'clamp(3rem,5.5vw,4.2rem)',
            color: '#fff', mb: 1,
          }}
        >
          {PRODUCT.name}
        </Typography>
        <Typography
          sx={{
            fontWeight: 800, color: BRAND.greenLight, lineHeight: 1.2,
            fontSize: compact ? '1rem' : '1.15rem',
            textTransform: 'uppercase', letterSpacing: '.4px', mb: 2.5,
            maxWidth: 400,
          }}
        >
          {PRODUCT.fullName}
        </Typography>

        <Typography sx={{ fontSize: 14.5, lineHeight: 1.75, color: 'rgba(255,255,255,.8)', maxWidth: 440, mb: compact ? 3.5 : 4.5 }}>
          {PRODUCT.blurb}
        </Typography>

        {/* ── Facility identity, once it exists ── */}
        {(facilityName || facilityLogo) && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 2, mb: 4,
              px: 2, py: 1.75, borderRadius: '14px',
              background: 'rgba(255,255,255,.07)',
              border: '1px solid rgba(255,255,255,.15)',
              backdropFilter: 'blur(8px)',
            }}
          >
            {facilityLogo ? (
              <Box
                component="img" src={facilityLogo} alt=""
                sx={{ width: 44, height: 44, borderRadius: '10px', objectFit: 'contain', background: '#fff', p: .5, flexShrink: 0 }}
              />
            ) : (
              <Box sx={{ width: 44, height: 44, borderRadius: '10px', background: 'rgba(255,255,255,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <HospitalIcon sx={{ fontSize: 24, color: '#fff' }} />
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'rgba(255,255,255,.5)', fontWeight: 700 }}>
                Licensed to
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1.3 }} noWrap>
                {facilityName}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Capability strip — the navy band from the marketing hero ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1, mb: compact ? 3.5 : 4.5 }}>
          {PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <Box
                key={p.title}
                sx={{
                  px: 1.5, py: 1.5, borderRadius: '11px',
                  background: 'rgba(255,255,255,.06)',
                  border: '1px solid rgba(255,255,255,.12)',
                  transition: 'background .2s ease, transform .2s ease',
                  '&:hover': { background: 'rgba(255,255,255,.11)', transform: 'translateY(-2px)' },
                }}
              >
                <Icon sx={{ fontSize: 19, color: BRAND.greenLight, mb: .75, display: 'block' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#fff', lineHeight: 1.25 }}>
                  {p.title}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: 'rgba(255,255,255,.55)', lineHeight: 1.3 }}>
                  {p.sub}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {children}

        {/* ── What the system actually does ──
             Replaces the old install-base statistic. A buyer running setup
             cares what the software will do for their hospital, not how many
             other hospitals bought it. */}
        <Box sx={{ pt: 3.5, borderTop: '1px solid rgba(255,255,255,.12)' }}>
          <Typography
            sx={{
              fontSize: 10.5, fontWeight: 800, letterSpacing: '1.4px',
              textTransform: 'uppercase', color: BRAND.greenLight, mb: 2,
            }}
          >
            What {PRODUCT.name} does
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            {NARRATION.map((n) => (
              <Box key={n.title} sx={{ display: 'flex', gap: 1.5 }}>
                {/* Small green rule instead of a bullet glyph — reads as
                    structure rather than as a list of features. */}
                <Box
                  sx={{
                    width: 3, borderRadius: 2, flexShrink: 0, mt: .35, mb: .35,
                    background: `linear-gradient(180deg, ${BRAND.greenLight}, transparent)`,
                  }}
                />
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.4, mb: .35 }}>
                    {n.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,.62)', lineHeight: 1.65 }}>
                    {n.body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Outcome figures ── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 2,
            mt: 3.5, pt: 3, borderTop: '1px solid rgba(255,255,255,.12)',
          }}
        >
          {OUTCOMES.map((o) => (
            <Box key={o.label} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 21, fontWeight: 900, color: BRAND.greenLight, lineHeight: 1.1 }}>
                {o.value}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: 'rgba(255,255,255,.55)', lineHeight: 1.35 }}>
                {o.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography sx={{ mt: 3.5, fontSize: 11.5, color: 'rgba(255,255,255,.38)' }}>
          © {new Date().getFullYear()} {PRODUCT.vendor} · {PRODUCT.name}
        </Typography>
      </Box>
    </Box>
  );
}

export default BrandPanel;
